import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import {
  useSetLoyaltyScanAndPayMutation,
  useSetSelectedScanAndPayPaymentMethodMutation,
} from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useAuthContext } from 'state/auth';
import { usePaymentContext } from 'state/payment';
import { toast } from 'utils/toast';

import { getSortedValidPaymentMethods, hasValidScanAndPayPaymentMethodOption } from '../utils';
import { createBaseBarcode, createScanAndPayBarcode, getForterToken } from '../utils/barcode-utils';

import { IUseScanAndPay } from './types';

/**
 *
 * useScanAndPay contains logic for checking &
 * updating TH scan & pay feature for a specific user
 * as well as selected payment method for scan & pay
 *
 */
export const useScanAndPay = (): IUseScanAndPay => {
  const { user, loading: userLoading, refreshCurrentUser } = useAuthContext();
  const { formatMessage } = useIntl();
  const {
    activeCard,
    refetchActiveCard,
    loyaltyDetails,
    loading: loyaltyDetailsLoading,
  } = useLoyaltyActiveCard();
  const { paymentMethods, loading: paymentsLoading } = usePaymentContext();

  const scanAndPayEnabledForUser = !!loyaltyDetails?.scanAndPay;
  const selectedPaymentMethodId = user?.details.defaultScanAndPayAccountIdentifier ?? '';

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingSelectedPaymentMethod, setUpdatingSelectedPaymentMethod] = useState(false);

  const [
    setLoyaltyScanAndPayMutation,
    { loading: setLoyaltyScanAndPayLoading },
  ] = useSetLoyaltyScanAndPayMutation({
    onCompleted: async () => {
      // refreshes user's selected scan & pay payment method
      await refreshCurrentUser();
      // refreshes user's scan & pay feature status
      await refetchActiveCard();
      setUpdatingStatus(false);
      setUpdatingSelectedPaymentMethod(false);
    },
  });

  const [
    setSelectedScanAndPayPaymentMethodMutation,
    { loading: setSelectedPaymentMethodLoading },
  ] = useSetSelectedScanAndPayPaymentMethodMutation({
    onCompleted: async () => {
      await refreshCurrentUser();
      setUpdatingSelectedPaymentMethod(false);
    },
  });

  const updateScanAndPayStatus = useCallback(
    async (newStatus: boolean, defaultPaymentMethodId: string = '') => {
      setUpdatingStatus(true);
      setUpdatingSelectedPaymentMethod(true);
      const cardId = activeCard?.cardId ?? '';
      return await setLoyaltyScanAndPayMutation({
        variables: { cardId, scanAndPay: newStatus, defaultPaymentMethodId },
      });
    },
    [activeCard, setLoyaltyScanAndPayMutation]
  );

  const updateSelectedScanAndPayPaymentMethod = useCallback(
    async (scanAndPayPaymentMethodId: string) => {
      setUpdatingSelectedPaymentMethod(true);
      await setSelectedScanAndPayPaymentMethodMutation({
        variables: { scanAndPayPaymentMethodId },
      });
      toast.success(formatMessage({ id: 'scanAndPayPaymentMethodUpdated' }));
    },
    [formatMessage, setSelectedScanAndPayPaymentMethodMutation]
  );

  const userHasValidScanAndPayPaymentMethodOption = hasValidScanAndPayPaymentMethodOption(
    paymentMethods
  );

  const { selectedPaymentMethod, nonSelectedPaymentMethods } = useMemo(
    () =>
      getSortedValidPaymentMethods({
        paymentMethods,
        selectedPaymentMethodId,
      }),
    [paymentMethods, selectedPaymentMethodId]
  );

  /**
   * TEMP: Store scan & pay barcode in context
   * Need to do this bc loyalty active card is a hook and not context
   * so pulling this in separately is causing issues with the barcode
   * not updating as expected.
   */
  const baseBarcode = createBaseBarcode(activeCard?.barcode);
  const userId = user?.cognitoId;
  const forterToken = getForterToken();
  const scanAndPayBarcode = scanAndPayEnabledForUser
    ? createScanAndPayBarcode({
        baseBarcode,
        forterToken,
        paymentMethodId: selectedPaymentMethodId,
        userId,
      })
    : baseBarcode;

  return {
    loadingStatus: loyaltyDetailsLoading || !loyaltyDetails,
    scanAndPayBarcode,
    scanAndPayEnabledForUser,
    updateScanAndPayStatus,
    updatingStatus: updatingStatus || setLoyaltyScanAndPayLoading,
    loadingSelectedPaymentMethod: userLoading || paymentsLoading,
    nonSelectedPaymentMethods,
    selectedPaymentMethodId,
    selectedPaymentMethod,
    updateSelectedScanAndPayPaymentMethod,
    updatingSelectedPaymentMethod: updatingSelectedPaymentMethod || setSelectedPaymentMethodLoading,
    userHasValidScanAndPayPaymentMethodOption,
  };
};
